import { API_ENDPOINT } from "../react-app-env.d";
import { getJwtToken, setJwtToken } from "./token";
import { getClientSessionId, newRequestId } from "./session";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { noteSaveRequest } from "./askReturn";

// --- API breadcrumbs ----------------------------------------------------------
// A rolling window of the last N API calls. When something breaks, the request
// that led up to it is usually more diagnostic than the stack trace — so the
// error shipper and the Report-a-bug capture both attach this. Kept in memory
// only; never persisted, never sent anywhere on its own.
export interface ApiBreadcrumb {
    method: string;
    url: string;
    status: number;
    durationMs: number;
    at: string;
    /** Set when the same call repeated back-to-back; see recordBreadcrumb. */
    repeatCount?: number;
    /** X-Request-Id sent with this call — paste into System → Error Logs to find the server's side of it. */
    requestId?: string;
    /** What the backend actually said, for non-2xx responses. Without this a report just says "500". */
    errorMessage?: string;
    /** What we SENT, for failed calls only. Redacted — see redactSensitive. */
    requestPayload?: string;
    /** What came BACK, for failed calls only. The full body, not just the message. */
    responsePayload?: string;
}

/**
 * The shape every request helper resolves with: the HTTP status plus the parsed
 * JSON body. `T` types the SUCCESS body — callers must still check `status`
 * before trusting it, since error responses carry `{ message: string }` instead.
 * Defaults to `any` so existing untyped call sites compile unchanged.
 */
export interface ApiResponse<T = any> {
    status: number;
    body: T;
}

const MAX_BREADCRUMBS = 25;
const MAX_PAYLOAD_CHARS = 2000;
const breadcrumbs: ApiBreadcrumb[] = [];

/**
 * Field names whose values must never reach a bug report. Payload capture is
 * genuinely useful for debugging a failed call, but a login or password-reset
 * body would otherwise write a real credential into a log a whole team can read.
 */
const SENSITIVE_KEY = /pass|token|secret|salt|auth|credential|otp|pin|cvv|card|ssn|iban/i;

/**
 * Deep-copy a payload with sensitive values replaced. Returns a compact JSON
 * string, truncated — a breadcrumb is a clue, not an archive.
 */
const redactSensitive = (value: any, depth: number = 0): any => {
    if (value === null || value === undefined) return value;
    if (depth > 6) return "[deep]";

    if (Array.isArray(value)) {
        return value.slice(0, 50).map(item => redactSensitive(item, depth + 1));
    }

    if (typeof value === "object") {
        const out: Record<string, any> = {};
        for (const [key, child] of Object.entries(value)) {
            out[key] = SENSITIVE_KEY.test(key) ? "[redacted]" : redactSensitive(child, depth + 1);
        }
        return out;
    }

    return value;
};

/**
 * Render a request/response body for a breadcrumb: redacted, stringified, capped.
 * FormData (file uploads) is summarised rather than serialised.
 */
const capturePayload = (body: any): string | undefined => {
    if (body === null || body === undefined) return undefined;

    try {
        if (body instanceof FormData) {
            const keys: string[] = [];
            body.forEach((_value, key) => keys.push(key));
            return `[FormData: ${keys.join(", ") || "empty"}]`;
        }
        if (typeof body === "string") return body.slice(0, MAX_PAYLOAD_CHARS);
        return JSON.stringify(redactSensitive(body)).slice(0, MAX_PAYLOAD_CHARS);
    } catch {
        return "[unserialisable]";
    }
};

/**
 * Append a call, collapsing consecutive repeats of the same call into a single
 * entry with a count.
 *
 * Several widgets poll on a timer (unread-count, unrated-missions, ...). On a
 * page left open for a few minutes those pollers alone would fill all 25 slots
 * and evict the calls that actually explain what the user was doing — which is
 * the whole reason this buffer exists. Collapsing keeps the fact that polling
 * happened without letting it crowd out the signal.
 */
const recordBreadcrumb = (entry: ApiBreadcrumb): void => {
    const last = breadcrumbs[breadcrumbs.length - 1];
    if (last && last.method === entry.method && last.url === entry.url && last.status === entry.status) {
        last.repeatCount = (last.repeatCount ?? 1) + 1;
        last.at = entry.at;
        last.durationMs = entry.durationMs;
        // Keep the most recent id so the link still resolves to a real server
        // log line. Note a call that succeeds then fails does NOT coalesce —
        // status is part of the match — so failures always keep their own id.
        last.requestId = entry.requestId;
        return;
    }

    breadcrumbs.push(entry);
    if (breadcrumbs.length > MAX_BREADCRUMBS) {
        breadcrumbs.shift();
    }
};

/**
 * The last (up to 25) API calls this tab made, oldest first.
 * @returns A copy of the breadcrumb buffer.
 */
export const getApiBreadcrumbs = (): ApiBreadcrumb[] => [...breadcrumbs];

// --- Token refresh coalescing -------------------------------------------------
// The access token lives in localStorage (shared by every tab) and, in prod,
// expires every minute. Without coalescing, N open tabs each fire their own
// refresh on expiry. We collapse that to a single refresh:
//   * within a tab: all 401'd requests await one shared in-flight promise;
//   * across tabs:  the Web Locks API serializes the refresh, so tabs that grab
//                   the lock after the winner just reuse the already-updated
//                   localStorage token instead of hitting the network again.
let inFlightRefresh: Promise<string | null> | null = null;

const fetchNewToken = async (expiredToken: string | null): Promise<string | null> => {
    // Another request/tab may have refreshed while we waited — if the stored
    // token already changed, reuse it rather than refreshing again.
    const latest = getJwtToken();
    if (latest && latest !== expiredToken) {
        return latest;
    }
    const refreshTokenResponse = await getReq('users/refresh-token');
    if (refreshTokenResponse && refreshTokenResponse.status === 200 && refreshTokenResponse.body?.accessToken) {
        setJwtToken(refreshTokenResponse.body.accessToken);
        return refreshTokenResponse.body.accessToken;
    }
    return null;
};

const coalescedRefresh = (expiredToken: string | null): Promise<string | null> => {
    if (inFlightRefresh) {
        return inFlightRefresh;
    }
    const run = async (): Promise<string | null> => {
        const locks = typeof navigator !== "undefined" ? (navigator as any).locks : undefined;
        if (locks && typeof locks.request === "function") {
            return await locks.request("crx-token-refresh", () => fetchNewToken(expiredToken));
        }
        return await fetchNewToken(expiredToken);
    };
    inFlightRefresh = run().finally(() => { inFlightRefresh = null; });
    return inFlightRefresh;
};

// Hard logout: clear the shared token, tell every other tab to bail to /login
// too (so the user isn't left with half-dead tabs once the shared session is
// gone), then redirect this tab. The refresh-token cookie is HttpOnly and is
// cleared server-side by /users/logout or expires on its own — js-cookie cannot
// touch it, so we don't pretend to remove it here.
const forceLogout = (): void => {
    setJwtToken("");
    try {
        const logoutChannel = new BroadcastChannel("logout-channel");
        logoutChannel.postMessage({ type: "logout" });
        logoutChannel.close();
    } catch {
        // BroadcastChannel unsupported — other tabs recover on their next request.
    }
    window.location.href = "/login";
};

/** Reads the filename out of a Content-Disposition header (filename="…" or filename*=UTF-8''…). */
const filenameFromDisposition = (header: string | null): string | undefined => {
    const match = header?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    if (!match) return undefined;
    try {
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
};


// --- Loading line -------------------------------------------------------------
// The thin line under the header shows while a call the person caused is
// running: one that starts within a second of a click, a key, a back/forward,
// or another such call (a save's refetch). Timers that refresh in the
// background (bell, badges, sit-reps) start on their own, so they never make
// the line blink — new pollers included, with nothing to mark.
const CAUSED_WITHIN_MS = 1000;
let lastCauseAt = Date.now(); // opening the app counts as a cause
let causedCalls = 0;
const loadingListeners = new Set<() => void>();

if (typeof window !== "undefined") {
    const noteCause = () => { lastCauseAt = Date.now(); };
    window.addEventListener("pointerdown", noteCause, true);
    window.addEventListener("keydown", noteCause, true);
    window.addEventListener("popstate", noteCause);
}

const changeCausedCalls = (delta: number): void => {
    causedCalls += delta;
    lastCauseAt = Date.now();
    loadingListeners.forEach((listener) => listener());
};

/** Calls the person caused that are still running — what the loading line follows. */
export const getCausedCalls = (): number => causedCalls;

export const subscribeLoading = (listener: () => void): (() => void) => {
    loadingListeners.add(listener);
    return () => { loadingListeners.delete(listener); };
};

// While a form Ask filled in is open, its saves are tagged so the record's
// history shows "via Ask" (a label only; the server grants nothing for it).
let askDraftOrigin = false;
export const setAskDraftOrigin = (isOn: boolean): void => {
    askDraftOrigin = isOn;
};

const request = async (
    method: string,
    url: string,
    body: any = null,
    isRetry: boolean = false,
    responseType: "json" | "blob" = "json",
    // the caller handles a refusal itself: no permission toast, no redirect to /denied (Ask CRX hides permission gaps)
    quiet: boolean = false,
): Promise<any> => {
    let token = getJwtToken();
    // Correlation ids travel with every call. The server prefixes the request id
    // onto each log line it writes while handling this call, so a failed call in
    // a bug report links straight to the server-side stack trace. A retry is a
    // new call and gets its own id.
    const requestId = newRequestId();
    let headers: any = {
        'Content-Type': 'application/json',
        'X-Request-Id': requestId,
        'X-Session-Id': getClientSessionId(),
    };

    if (token) {
        headers.Authorization = token;
    }
    if (askDraftOrigin && method !== 'GET') {
        headers['X-CRX-Origin'] = 'ask-draft';
    }

    const options: any = {
        method,
        headers: headers,
        credentials: 'include'
    };

    if (body instanceof FormData) {
        // For file uploads, don't set Content-Type header
        delete options.headers['Content-Type'];
        options.body = body;
    } else {
        if (body) {
            // For non-file data, set Content-Type header to JSON
            options.body = JSON.stringify(body);
        }
    }

    const makeRequest = async (): Promise<any> => {
        const startedAt = Date.now();
        try {
            let response = await fetch(process.env.REACT_APP_API_ENDPOINT + url, options);
            if (responseType === "blob" && response.ok) {
                const blob = await response.blob();
                recordBreadcrumb({
                    method,
                    url,
                    status: response.status,
                    durationMs: Date.now() - startedAt,
                    at: new Date(startedAt).toISOString(),
                    requestId,
                });
                return { status: response.status, body: blob, filename: filenameFromDisposition(response.headers.get("Content-Disposition")) };
            }
            // A failed download still answers with the API's JSON error (or nothing readable).
            let responseBody = responseType === "blob"
                ? await response.json().catch(() => ({ message: response.statusText }))
                : await response.json();
            const responseData = {
                status: response.status,
                body: responseBody
            };
            const failed = response.status >= 400;
            // the save a form opened from Ask waits for: Ask comes back to the conversation
            if (!failed && response.status < 300 && method !== 'GET') noteSaveRequest(method, url);
            recordBreadcrumb({
                method,
                url,
                status: response.status,
                durationMs: Date.now() - startedAt,
                at: new Date(startedAt).toISOString(),
                requestId,
                // Capture what the backend actually said. Without this a bug
                // report can only tell a triager "there was a 500".
                errorMessage: failed
                    ? extractErrorMessage(responseBody, "").slice(0, 300) || undefined
                    : undefined,
                // Bodies only for failures: on a healthy call they are noise, and
                // keeping 25 of them in memory for every tab would not be free.
                requestPayload: failed ? capturePayload(body) : undefined,
                responsePayload: failed ? capturePayload(responseBody) : undefined,
            });
            // you don't have permission to call this api
            if (responseData.status === 403 && responseData.body && responseData.body.message === 'You do not have permissions to perform this action, please contact admin') {
                if (!quiet) toast.error(responseData.body.message);
                return {
                    status: response.status,
                    body: {}
                };
            }
            
            if (responseData.status === 401 && responseData.body && (responseData.body.message === 'refresh token invalid')) {
                forceLogout();
                return { status: 401, body: { message: "Session expired. Please log in again." } };
            }
            // you don't have permission to call this page
            if (!quiet && responseData.status === 403 && responseData.body && responseData.body.message === 'Access denied') {
                // Diagnostic: log the offending URL + record it in
                // sessionStorage so it survives the page redirect — a hard
                // navigation otherwise wipes the DevTools network/console
                // log and you can't tell which endpoint triggered the bounce.
                // After a redirect, run `sessionStorage.getItem('lastAccessDeniedUrl')`
                // in the console to see it.
                try {
                    // eslint-disable-next-line no-console
                    console.error(`[Access denied redirect] triggered by: ${method.toUpperCase()} ${url}`);
                    if (typeof sessionStorage !== "undefined") {
                        sessionStorage.setItem(
                            "lastAccessDeniedUrl",
                            JSON.stringify({ method, url, at: new Date().toISOString() }),
                        );
                    }
                } catch {
                    /* ignore — diagnostic only */
                }
                // Guard against an infinite redirect loop: when /denied itself
                // makes a permission-checked API call (via header/layout
                // widgets like the unrated-missions badge), this same handler
                // would re-fire window.location.href which forces a hard
                // reload. With a 403-bearing endpoint mounted on the layout,
                // each reload re-triggers the call → re-redirect → loop.
                // Just return the response so the caller can ignore it; we're
                // already on the right page.
                // The auth flow runs on a pre-2FA token that fails permission
                // checks by design — bouncing those pages to /denied would
                // eject a user who merely refreshed /2fa. Their pages handle
                // the 403 themselves.
                const authFlowPaths = ["/login", "/2fa", "/forgot-password", "/redeem-invite"];
                if (typeof window !== "undefined"
                    && window.location.pathname !== "/denied"
                    && !authFlowPaths.includes(window.location.pathname)) {
                    window.location.href = "/denied";
                }
                return { status: 403, body: { message: "Access denied" } };
            }

            // if there is no data found
            if (responseData.status === 404 && responseData.body === 'Not Found') {
                window.location.href = "/not-found";
                return { status: 404, body: { message: "Not Found" } };
            }

            // Access token expired, OR valid-but-revoked server-side (the auth gate
            // returns 401 "Unauthorized" when a token is no longer in Redis). Both
            // are recoverable by refreshing, so handle them identically.
            if (responseData.status === 401 && responseData.body &&
                (responseData.body.message === 'Token Expired' || responseData.body.message === 'Unauthorized')) {
                // Already refreshed once for this request and still 401 → the session
                // is genuinely gone; stop looping and log out cleanly.
                if (isRetry) {
                    forceLogout();
                    return { status: 401, body: { message: "Session expired. Please log in again." } };
                }
                const newToken = await coalescedRefresh(token);
                if (newToken) {
                    return await request(method, url, body, true, responseType, quiet);
                }
                forceLogout();
                return { status: 401, body: { message: "Session expired. Please log in again." } };
            }

            return {
                status: response.status,
                body: responseBody
            };
        } catch (e: any) {
            console.error(e);
            recordBreadcrumb({
                method,
                url,
                status: 0,
                durationMs: Date.now() - startedAt,
                at: new Date(startedAt).toISOString(),
                requestId,
                errorMessage: String(e?.message ?? e).slice(0, 300),
                requestPayload: capturePayload(body),
            });
            const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
            const message = isOffline
                ? "You appear to be offline. Please check your connection and try again."
                : "Network error. Please check your connection and try again.";
            toast.error(message);
            return { status: 0, body: { message } };
        }
    };

    const isCaused = causedCalls > 0 || Date.now() - lastCauseAt < CAUSED_WITHIN_MS;
    if (isCaused) changeCausedCalls(1);
    try {
        return await makeRequest();
    } finally {
        if (isCaused) changeCausedCalls(-1);
    }
};


/**
 * GET for PUBLIC (no-login) pages — the Live Mission Link tracker. Differs
 * from getReq on purpose: sends no Authorization header, and never runs the
 * 401/403/404 redirect handling above (an anonymous visitor must see the
 * page's own "link expired" state, not be bounced to /login or /denied).
 */
export const publicGetReq = async <T = any>(url: string): Promise<ApiResponse<T>> => {
    const startedAt = Date.now();
    const requestId = newRequestId();
    try {
        const response = await fetch(process.env.REACT_APP_API_ENDPOINT + url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'X-Request-Id': requestId,
                'X-Session-Id': getClientSessionId(),
            },
        });
        const responseBody = await response.json().catch(() => ({}));
        recordBreadcrumb({
            method: 'GET',
            url,
            status: response.status,
            durationMs: Date.now() - startedAt,
            at: new Date(startedAt).toISOString(),
            requestId,
        });
        return { status: response.status, body: responseBody };
    } catch (e: any) {
        recordBreadcrumb({
            method: 'GET',
            url,
            status: 0,
            durationMs: Date.now() - startedAt,
            at: new Date(startedAt).toISOString(),
            requestId,
            errorMessage: String(e?.message ?? e).slice(0, 300),
        });
        return { status: 0, body: { message: "Network error" } as any };
    }
}

/**
 * POST for PUBLIC (no-login) pages — signing a contract from its link. Same
 * rules as publicGetReq: no Authorization header, no redirect handling.
 */
export const publicPostReq = async <T = any>(url: string, data: any): Promise<ApiResponse<T>> => {
    const startedAt = Date.now();
    const requestId = newRequestId();
    try {
        const response = await fetch(process.env.REACT_APP_API_ENDPOINT + url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Request-Id': requestId,
                'X-Session-Id': getClientSessionId(),
            },
            body: JSON.stringify(data ?? {}),
        });
        const responseBody = await response.json().catch(() => ({}));
        recordBreadcrumb({ method: 'POST', url, status: response.status, durationMs: Date.now() - startedAt, at: new Date(startedAt).toISOString(), requestId });
        return { status: response.status, body: responseBody };
    } catch (e: any) {
        recordBreadcrumb({ method: 'POST', url, status: 0, durationMs: Date.now() - startedAt, at: new Date(startedAt).toISOString(), requestId, errorMessage: String(e?.message ?? e).slice(0, 300) });
        return { status: 0, body: { message: "Network error" } as any };
    }
}

/**
 * File upload for PUBLIC (no-login) pages — a provider sending a certificate
 * from their Training link. Same rules as publicPostReq: no Authorization
 * header, no redirect handling; the browser sets the multipart boundary.
 */
export const publicUploadReq = async <T = any>(url: string, data: FormData): Promise<ApiResponse<T>> => {
    const startedAt = Date.now();
    const requestId = newRequestId();
    try {
        const response = await fetch(process.env.REACT_APP_API_ENDPOINT + url, {
            method: 'POST',
            headers: {
                'X-Request-Id': requestId,
                'X-Session-Id': getClientSessionId(),
            },
            body: data,
        });
        const responseBody = await response.json().catch(() => ({}));
        recordBreadcrumb({ method: 'POST', url, status: response.status, durationMs: Date.now() - startedAt, at: new Date(startedAt).toISOString(), requestId });
        return { status: response.status, body: responseBody };
    } catch (e: any) {
        recordBreadcrumb({ method: 'POST', url, status: 0, durationMs: Date.now() - startedAt, at: new Date(startedAt).toISOString(), requestId, errorMessage: String(e?.message ?? e).slice(0, 300) });
        return { status: 0, body: { message: "Network error" } as any };
    }
}

/** Options for a call: `quiet` = the caller handles a refusal (no permission toast or redirect). */
export interface RequestOptions { quiet?: boolean }

export const getReq = async <T = any>(url: string, options?: RequestOptions): Promise<ApiResponse<T>> => {
    return await request('GET', url, null, false, "json", !!options?.quiet);
}

export const postReq = async <T = any>(url: string, data: any, options?: RequestOptions): Promise<ApiResponse<T>> => {
    return await request('POST', url, data, false, "json", !!options?.quiet);
}
export const UploadReq = async <T = any>(url: string, data: FormData, options?: RequestOptions): Promise<ApiResponse<T>> => {
    return await request('POST', url, data, false, "json", !!options?.quiet);
}

export const putReq = async <T = any>(url: string, data: any, options?: RequestOptions): Promise<ApiResponse<T>> => {
    return await request('PUT', url, data, false, "json", !!options?.quiet);
}

export const patchReq = async <T = any>(url: string, data: any): Promise<ApiResponse<T>> => {
    return await request('PATCH', url, data);
}

export const deleteReq = async <T = any>(url: string, options?: RequestOptions): Promise<ApiResponse<T>> => {
    return await request('DELETE', url, null, false, "json", !!options?.quiet);
}

export interface DownloadResponse {
    status: number;
    /** The file, when the download succeeded. */
    blob?: Blob;
    /** The server's filename from Content-Disposition, when it sent one. */
    filename?: string;
    /** The API's error body, when it didn't. */
    body?: any;
}

/**
 * GET a file (a streamed download) with the same auth, token refresh and
 * permission handling as getReq. Network errors are already toasted (status 0).
 */
export const downloadReq = async (url: string): Promise<DownloadResponse> => {
    const result = await request('GET', url, null, false, 'blob');
    return result.body instanceof Blob
        ? { status: result.status, blob: result.body, filename: result.filename }
        : { status: result.status, body: result.body };
}

/** Hands a Blob to the browser as a file download. */
export const saveBlob = (blob: Blob, filename: string): void => {
    const href = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(href);
}

/**
 * Extracts a human-readable error message from an API response body.
 * Handles both { message: string } objects and plain string bodies.
 */
export const extractErrorMessage = (body: any, fallback: string): string => {
    if (!body) return fallback;
    if (typeof body === "string") return body !== "" ? body : fallback;
    if (typeof body?.message === "string" && body.message !== "") return body.message;
    return fallback;
};

export default request;